import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDebounce } from "@/hooks/useDebounce";
import { priorityService } from "@/services/priorityService";
import { userService } from "@/services/userService";

/**
 * Reference data (users) + per-user Focus/Queue load for the priority form —
 * split out of PriorityForm to keep that file under the repo's line limit.
 */
export function usePriorityFormData(assignedUserPids: string[], editingPid?: string) {
    const [userSearch, setUserSearch] = useState("");
    const debouncedUserSearch = useDebounce(userSearch, 300);

    const { data: usersResponse, isLoading: isLoadingUsers } = useQuery({
        queryKey: ["users-list", debouncedUserSearch],
        queryFn: () => userService.getCompanyUsers(debouncedUserSearch, 1, 20),
    });
    const users = useMemo(() => usersResponse?.data ?? [], [usersResponse]);

    // Fetch Focus/Queue load for candidate + assigned users to prevent over-assignment inline.
    const loadUserPids = useMemo(
        () => [...new Set([...users.map((u) => u.pid), ...assignedUserPids])],
        [users, assignedUserPids]
    );
    const { data: assignmentLoad } = useQuery({
        queryKey: ["assignment-load", editingPid ?? null, [...loadUserPids].sort().join(",")],
        queryFn: () => priorityService.getAssignmentLoad(loadUserPids, editingPid),
        enabled: loadUserPids.length > 0,
    });
    const loadByPid = useMemo(
        () => new Map((assignmentLoad ?? []).map((l) => [l.userPid, { focus: l.focus, queue: l.queue }])),
        [assignmentLoad]
    );

    return { users, isLoadingUsers, userSearch, setUserSearch, loadByPid };
}
